import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { CaretDown, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";

import {
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./AgentSteps";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

interface ThinkingStateProps {
  /** undefined = no assistant message yet (phase 0) */
  message?: AskPunttMessage;
  /**
   * True when the user's latest message had file attachments and the backend
   * is still in its pre-agent read phase. Fills the silent pause while
   * `_pre_read_attachments` runs before any agent step fires.
   */
  precedingUserHasAttachments?: boolean;
  /** True once the response has rendered; keeps a collapsed status summary. */
  completed?: boolean;
}

function getPhase(
  message?: AskPunttMessage,
  precedingUserHasAttachments?: boolean,
): {
  phase: number;
  text: string;
} {
  const hasSteps = (message?.agentSteps?.length ?? 0) > 0;
  const hasSearching = message?.searchProgress?.some(
    (s) => s.status === "searching",
  );
  const hasStreamingContent = !!message?.streamingContent;

  if (hasStreamingContent) {
    return { phase: 4, text: "Writing response..." };
  }

  if (hasSteps) {
    return { phase: 3, text: "Analyzing..." };
  }

  if (hasSearching) {
    const searchingSource = message?.searchProgress?.find(
      (s) => s.status === "searching",
    );
    return {
      phase: 2,
      text: `Researching ${searchingSource?.source ?? ""}...`.trim(),
    };
  }

  if (precedingUserHasAttachments) {
    return { phase: 1, text: "Reading attachments..." };
  }

  if (!message) {
    return { phase: 0, text: "Thinking" };
  }

  return { phase: 1, text: "Starting up..." };
}

export function ThinkingState({
  message,
  precedingUserHasAttachments,
  completed = false,
}: ThinkingStateProps) {
  const { text } = getPhase(message, precedingUserHasAttachments);
  const [displayText, setDisplayText] = useState(text);
  const [fading, setFading] = useState(false);
  const prevTextRef = useRef(text);
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const expanded = manualToggle ?? !completed;
  const summaryText = completed ? "Started response" : displayText;

  useEffect(() => {
    if (text !== prevTextRef.current) {
      setFading(true);
      const timer = setTimeout(() => {
        setDisplayText(text);
        setFading(false);
        prevTextRef.current = text;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [text]);

  return (
    <div className="my-3">
      <div
        data-testid="ask-puntt-thinking-state"
        className={`overflow-hidden ${interactiveSurfaceClasses({ expandable: true, muted: true })}`}
      >
        <button
          data-testid="thinking-state-toggle"
          type="button"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} thinking status`}
          onClick={() => setManualToggle(!expanded)}
          className={STATUS_PANEL_TOGGLE_MUTED_CLASS}
        >
          {completed ? (
            <CheckCircle
              size={16}
              weight="fill"
              className="shrink-0 text-malachite-600"
              aria-hidden
            />
          ) : (
            <CircleNotch
              size={16}
              className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              aria-hidden
            />
          )}
          <Text
            size="1"
            weight="medium"
            className={`text-puntt-neutral-gray-12 transition-opacity duration-150 ${fading ? "opacity-0" : "opacity-100"}`}
          >
            {summaryText}
          </Text>
          <CaretDown
            size={14}
            aria-hidden
            className={`ml-auto shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
          />
        </button>

        {expanded && (
          <div
            data-testid="thinking-state-items"
            className={STATUS_PANEL_ITEMS_MUTED_CLASS}
          >
            {/* An activity pulse, deliberately not a progress meter. The old
                "Phase N of 4" counter was inferred from the assistant's own
                message text — no pipeline reports it, the 4 was arbitrary, and
                it contradicted the artwork review's real (backend-driven)
                stages. `summaryText` above already says what is happening. */}
            <span className="flex gap-0.5 motion-reduce:hidden">
              <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
              <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
              <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
